import { IconButton } from "@material-ui/core";
import DeleteForeverIcon from "@material-ui/icons/DeleteForever";
import PersonAdd from "@material-ui/icons/PersonAdd";
import { nanoid } from "nanoid";
import { isEmpty, ifElse } from "ramda";
import { memo, useState } from "react";
import { UseFormMethods } from "react-hook-form";

import { Button } from "../Button";
import { Generator } from "../Generator";
import {
  CommonFormElProps,
  FormSchema,
  SimpleFormSchemaComponents,
} from "../types";

import styles from "./MultiplePerson.module.scss";

type Form = (id: string) => FormSchema<SimpleFormSchemaComponents>;

export type MultiplePersonProps = Pick<
  CommonFormElProps,
  "id" | "isComplex" | "defaultValue"
> & {
  readonly form: Form;
  readonly register: UseFormMethods["register"];
};

type Person = {
  readonly id: string;
  readonly form: FormSchema<SimpleFormSchemaComponents>;
};

const createForm = (id: string, form: Form): Person => {
  return {
    id,
    form: form(id),
  };
};

const createDefaultForm = ifElse(
  isEmpty,
  (_, form: Form) => [createForm(nanoid(), form)],
  (defaultValue: Record<string, Person>, form: Form) =>
    Object.keys(defaultValue).map((id) => createForm(id, form))
);

export const MultiplePerson = memo<MultiplePersonProps>(
  ({ defaultValue = [], form, register }) => {
    const [persons, setPerson] = useState<Array<Person>>(
      createDefaultForm(defaultValue, form)
    );
    const handleAddPerson = () => {
      setPerson([...persons, createForm(nanoid(), form)]);
    };
    const handleRemovePerson = (personId: string) => () => {
      setPerson(persons.filter((person: Person) => person.id !== personId));
    };

    return (
      <>
        {persons.map((personForm: Person) => (
          <Generator
            key={personForm.id}
            form={personForm.form}
            render={(generatedForm) => (
              <div className={styles.form}>
                {persons.length > 1 && (
                  <IconButton
                    className={styles.removeButton}
                    onClick={handleRemovePerson(personForm.id)}
                    name={`remove-button-${personForm.id}`}
                    ref={register}
                  >
                    <DeleteForeverIcon />
                  </IconButton>
                )}
                {generatedForm}
              </div>
            )}
          />
        ))}
        {persons.length < 10 && (
          <Button
            className={styles.addButton}
            onClick={handleAddPerson}
            startIcon={<PersonAdd />}
          >
            dodaj nową osobę
          </Button>
        )}
      </>
    );
  }
);
